import { Policy } from "@deathstar/types/northstar";
import { DateTime } from "@deathstar/types/util";
import { Alert, BillingPeriodBreakdownDialog, classNames, InputField, Popover, PrimaryButton, useSnackbar } from "@deathstar/ui";
import { ArrowTopRightOnSquareIcon, CurrencyDollarIcon, EllipsisVerticalIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { CircularProgress, Menu, MenuItem } from "@material-ui/core";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";
import { useAccountId } from "../../api/useAccountId";
import { NotFoundError, UnauthorizedError } from "../../api/util/exceptions";
import { ActionButton } from "../../components/action-button/ActionButton";
import { Dialog } from "../../components/dialog/Dialog";
import Unauthorized from "../../components/error-screens/Unauthorized";
import UncaughtException from "../../components/error-screens/UncaughtException";
import Loader from "../../components/loader/Loader";
import { queryClient } from "../../util/queryClient";
import { PolicyWithBillingInfo } from "../policies/policies-list/PoliciesList";
import NewPaymentMethodForm from "./NewPaymentMethodForm";
import PayBillForm from "./PaymentDialog";

export default function BillingPage() {
    const accountId = useAccountId();
    const { data, refetch, error } = api.billing.useProfile(accountId!);
    const { data: activePolicies, isLoading: isLoadingActivePolicies } = api.policies.useFind(accountId!, "active");
    const { data: inactivePolicies, isLoading: isLoadingInactivePolicies } = api.policies.useFind(accountId!, "inactive");

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const [payingBill, setPayingBill] = useState(false);
    const [paymentOptionsOpen, setPaymentOptionOpen] = useState(false);
    const [creatingPaymentMethod, setCreatingPaymentMethod] = useState(false);
    const [payBillSuccessful, setPayBillSuccessful] = useState(false);
    const autoPayEnabledPolicies = useMemo(() => {
        if (!activePolicies || !inactivePolicies) return [];
        return [
            ...activePolicies,
            ...inactivePolicies.filter((p) => new DateTime(p.expirationDate).isBetween(DateTime.today.subtractMonths(3), DateTime.today)),
        ].filter((policy) => policy.billingSettings?.autoPayEnabled);
    }, [activePolicies, inactivePolicies]);

    const { mutate: createBillingProfile, isPending } = api.billing.useCreateProfile(accountId!, {
        onSuccess: (data) => {
            queryClient.setQueryData(api.billing.queryKeys.profile(accountId!), data);
        },
    });

    const updateBillingProfile = api.billing.useUpdateProfile(accountId!, {
        onError() {
            useSnackbar.add("Request failed", { variant: "error" });
        },
    });

    if (error) {
        if (error instanceof UnauthorizedError) {
            return <Unauthorized />;
        }
        if (error instanceof NotFoundError) {
            return (
                <PrimaryButton disabled={isPending} onClick={() => createBillingProfile()}>
                    Set up billing profile
                </PrimaryButton>
            );
        }
        return <UncaughtException />;
    }
    if (!data) return <Loader />;

    return (
        <div className="">
            <PayBillForm
                open={payingBill && !isLoadingActivePolicies && !isLoadingInactivePolicies}
                onClose={(data) => {
                    if (data) {
                        setPayBillSuccessful(true);
                    }
                    setPayingBill(false);
                }}
            />
            <div className="space-y-4">
                <div className="flex flex-wrap gap-4">
                    <ActionButton
                        onClick={() => {
                            if (!data?.email) {
                                useSnackbar.add("Please provide a default billing email before proceeding", { variant: "error" });
                                return;
                            }
                            setCreatingPaymentMethod(true);
                        }}
                    >
                        <PlusIcon className="h-4 w-4 shrink-0" />
                        Add payment method
                    </ActionButton>
                    <ActionButton
                        onClick={(e) => {
                            if (autoPayEnabledPolicies?.length) {
                                setAnchorEl(e.currentTarget);
                                setPaymentOptionOpen(true);
                            } else {
                                setPayingBill(true);
                            }
                        }}
                    >
                        <CurrencyDollarIcon className="h-4 w-4 shrink-0" />
                        Make a payment
                    </ActionButton>
                    <Menu anchorEl={anchorEl} open={paymentOptionsOpen} onClose={() => setPaymentOptionOpen(false)}>
                        {autoPayEnabledPolicies?.map((policy) => (
                            <PolicyMenuOption key={policy.id} policy={policy} onClick={() => setPaymentOptionOpen(false)} />
                        ))}
                        <MenuItem
                            onClick={() => {
                                setPaymentOptionOpen(false);
                                setPayingBill(true);
                            }}
                        >
                            Other
                        </MenuItem>
                    </Menu>
                </div>
                {payBillSuccessful && (
                    <Alert
                        variant="success"
                        className="max-w-max"
                        action={
                            <button className="rounded-full p-1 hover:bg-stone-800/5" onClick={() => setPayBillSuccessful(false)}>
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        }
                    >
                        Payment successful!
                    </Alert>
                )}
                <InputField
                    label="Default billing email"
                    defaultValue={data.email}
                    className="max-w-sm"
                    onBlur={async (e) => {
                        try {
                            const email = e.target.value || "";
                            if (email === (data.email || "")) {
                                return;
                            }
                            if (!email) {
                                useSnackbar.add("Email cannot be empty", { variant: "error" });
                                e.target.value = data.email || "";
                                return;
                            }
                            await api.billing.updateProfile(accountId!, { email });
                            useSnackbar.add("Default billing email updated", { variant: "success" });
                            refetch();
                        } catch (err) {
                            console.error(err);
                            e.target.value = data.email || "";
                            useSnackbar.add("Failed to update default billing email", { variant: "error" });
                        }
                    }}
                />
                <hr />
                <p className="text-lg font-medium">Payment methods</p>
                <div className="flex w-full flex-col items-stretch gap-4 md:w-max md:flex-row">
                    {data.setupIntents.map((si) => (
                        <div key={si.id} className="space-y-4 rounded bg-stone-50/50 p-4 shadow">
                            <div className="flex justify-between gap-6">
                                <div className="flex items-baseline gap-1">
                                    <p className="text-sm text-stone-500 small-caps">US Bank Account (pending)</p>
                                </div>
                                <ActionButton
                                    onClick={() => {
                                        window.open(si.url, "_blank");
                                    }}
                                >
                                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                    Verify
                                </ActionButton>
                            </div>
                            <p className="font-mono">{si.value.padStart(16, "*")}</p>
                        </div>
                    ))}
                    {data.paymentMethods.map((pm) => (
                        <div
                            key={pm.id}
                            className={classNames(
                                "space-y-4 rounded border-2 bg-stone-50/50 p-4 shadow",
                                pm.isDefault ? "border-waypoint-blue" : "border-transparent"
                            )}
                        >
                            <div className="flex justify-between gap-6">
                                <div className="flex items-baseline gap-1">
                                    <p className="text-sm text-stone-500 small-caps">{pm.type.replaceAll("_", " ")}</p>
                                    {pm.isDefault && <span className="text-xs text-stone-500">(default)</span>}
                                </div>
                                <Popover className="">
                                    <Popover.Content className="w-max !p-0 !py-2">
                                        <Popover.Item
                                            className="!px-4 !py-2"
                                            disabled={pm.isDefault || updateBillingProfile.isPending}
                                            onClick={() => {
                                                updateBillingProfile.mutate({ defaultPaymentMethod: pm.id });
                                            }}
                                        >
                                            Set as default
                                        </Popover.Item>
                                        <Popover.Item
                                            className="!px-4 !py-2"
                                            onClick={async () => {
                                                await api.billing.removePaymentMethod(accountId!, pm.id);
                                                refetch();
                                            }}
                                        >
                                            Delete
                                        </Popover.Item>
                                    </Popover.Content>
                                    <Popover.Button className="rounded-full p-1 text-stone-600 hover:bg-stone-100">
                                        <EllipsisVerticalIcon className="h-5 w-5" />
                                    </Popover.Button>
                                </Popover>
                            </div>
                            <p className="font-mono">{pm.value.padStart(16, "*")}</p>
                        </div>
                    ))}
                    {!data.paymentMethods.length && !data.setupIntents.length && (
                        <p className="ml-2 text-stone-500">No payment methods found</p>
                    )}
                </div>
            </div>

            <Dialog
                open={creatingPaymentMethod}
                onClose={() => {
                    setCreatingPaymentMethod(false);
                }}
                className="overflow-y-auto p-4 pb-0"
            >
                <NewPaymentMethodForm
                    onSubmit={(setupIntent) => {
                        setCreatingPaymentMethod(false);
                        if (setupIntent) {
                            refetch();
                        }
                    }}
                />
            </Dialog>
        </div>
    );
}

function PolicyMenuOption({ policy, onClick }: { policy: PolicyWithBillingInfo; onClick: () => void }): JSX.Element | null {
    const accountId = useAccountId();
    const navigate = useNavigate();
    const [fullPolicy, setFullPolicy] = useState<Policy | null>(null);

    const [isPaymentPeriodDialogOpen, setIsPaymentPeriodDialogOpen] = useState(false);
    const { mutateAsync: findFullPolicyFn, isPending } = api.policies.useFindFullPolicyMutation();
    const { data: properties } = api.properties.useFind(accountId!);
    const { data: contacts } = api.contacts.useById(accountId!);
    const create = api.monthlyReports.useCreate(accountId!, policy.id);

    const { mutateAsync: findLayerMetadata } = api.policies.useFindLayerMetadataMutation();

    const { data: billingInfo, isLoading: isLoadingBillingInfo } = api.billing.useProfile(accountId!);
    const { mutateAsync: createPayment } = api.billing.useCreateAndPayInvoice(accountId!, {
        onSuccess() {
            useSnackbar.add("Payment was successful. It may take a moment show in the system.", {
                variant: "success",
                autoHideDuration: 15000,
            });
        },
        onError(error) {
            console.error(error);
        },
    });

    return (
        <>
            <MenuItem
                className="flex gap-2"
                key={policy.id}
                disabled={isPending}
                onClick={async () => {
                    if (!accountId) return;
                    const fullPolicy = await findFullPolicyFn({ accountId, policyId: policy.id });
                    setFullPolicy(fullPolicy);
                    setIsPaymentPeriodDialogOpen(true);
                }}
            >
                {isPending && <CircularProgress size="1em" classes={{ svg: " text-navigator-primary" }} />}
                <span>
                    Policy #{policy.number} - {policy.billingInfo?.companyName}
                </span>
            </MenuItem>
            {fullPolicy && (
                <BillingPeriodBreakdownDialog
                    policy={fullPolicy}
                    isLoading={false}
                    open={isPaymentPeriodDialogOpen}
                    onClose={() => {
                        onClick?.();
                        setIsPaymentPeriodDialogOpen(false);
                    }}
                    config={{
                        contacts,
                        waypointVersion: true,
                        async navigateToReporting({ policyId, year, month, report }) {
                            const href =
                                typeof year === "number" && typeof month === "number"
                                    ? `../monthly-reports/${policyId}/${year}/${month}`
                                    : `../monthly-reports/${policyId}`;
                            if (report) {
                                navigate(href);
                            } else if (typeof year === "number" && typeof month === "number") {
                                await create.mutate(
                                    { year, month },
                                    {
                                        onSuccess: () => navigate(href),
                                        onError: (error) => {
                                            if (error?.message?.includes("exists")) {
                                                navigate(href);
                                            }
                                        },
                                    }
                                );
                            }
                        },
                        updateBillingSettings: async (settings) => {
                            if (!accountId || !settings.policyId) return;
                            const billingSettings = await api.policies.updateBillingSettings(accountId, settings.policyId, settings);
                            const fullPolicy = await findFullPolicyFn({ accountId, policyId: policy.id });
                            setFullPolicy(fullPolicy);
                            return billingSettings;
                        },
                        getPolicyLayerMetadata(policy) {
                            return findLayerMetadata({
                                policy,
                                includeLatestLayersOnly: true,
                            });
                        },
                        billingInfo,
                        isLoadingBillingInfo,
                        createPayment,
                        enableActions: {
                            payNow: true,
                            reportNow: true,
                            viewStatement: true,
                            viewBillingPeriodDetails: false,
                            billingSettings: true,
                        },
                        onPaymentSuccess: async () => {
                            api.policies.cacheBust(policy.id);
                            const fullPolicy = await findFullPolicyFn({ accountId: accountId!, policyId: policy.id });
                            setFullPolicy(fullPolicy);
                            setIsPaymentPeriodDialogOpen(false);
                        },
                        properties,
                    }}
                />
            )}
        </>
    );
}
